import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent } from "../../components/ui/card";
import { toast } from "sonner";
import { API } from "../../App";

const PROJECT_TYPES = [
  { value: "creation", label: "Création" },
  { value: "transformation", label: "Transformation" },
  { value: "innovation", label: "Innovation" },
  { value: "restructuration", label: "Restructuration" },
  { value: "evenementiel", label: "Événementiel" },
  { value: "autre", label: "Autre" },
];

const STEPS = [
  "Identité du projet",
  "Contexte & objectifs",
  "Périmètre & contraintes",
  "Ressources",
  "Analyse SWOT",
];

const EMPTY_BRIEF = {
  name: "",
  type: "creation",
  sector: "",
  context: "",
  objectives: "",
  scope: "",
  constraints: "",
  target_audience: "",
  budget_range: "",
  duration_months: 6,
  team_size: 1,
  key_stakeholders: "",
  success_criteria: "",
  strengths: "",
  weaknesses: "",
  opportunities: "",
  threats: "",
};

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-slate-700 dark:text-slate-300">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-400 dark:text-slate-500">{hint}</p>}
  </div>
);

const NewProjectPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [brief, setBrief] = useState(EMPTY_BRIEF);
  const [creating, setCreating] = useState(false);

  const update = (key, value) => setBrief((b) => ({ ...b, [key]: value }));

  const canProceed = () => {
    if (step === 0) return brief.name.trim().length > 0;
    return true;
  };
const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await fetch(`${API}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ brief }),
      });

      if (response.ok) {
        const created = await response.json();
        toast.success("Projet créé, livrables générés");
        navigate(`/dashboard/projects/${created.project_id}`);
      } else {
        let detail = `Code ${response.status}`;
        try {
          const errorData = await response.json();
          detail = errorData.detail || JSON.stringify(errorData);
        } catch (e) {
          detail = await response.text();
        }
        toast.error(`Erreur : ${detail}`, { duration: 15000 });
        setCreating(false);
      }
    } catch (error) {
      toast.error(`Erreur réseau : ${error.message}`, { duration: 15000 });
      setCreating(false);
    }
  };
  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="new-project-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
          Décrivez votre projet
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          5 étapes, environ 10 minutes. Vous pourrez tout modifier ensuite.
        </p>
      </div>

      <div className="flex items-center gap-2">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1 flex flex-col items-center gap-2">
            <div className={`h-1.5 w-full rounded-full transition-colors ${i <= step ? "bg-blue-600" : "bg-slate-200 dark:bg-slate-700"}`} />
            <span className={`hidden sm:block text-[0.7rem] text-center leading-tight ${i === step ? "font-semibold text-slate-900 dark:text-white" : "text-slate-400 dark:text-slate-500"}`}>
              {label}
            </span>
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="p-7 sm:p-9 space-y-5">
          {step === 0 && (
            <>
              <Field label="Nom du projet">
                <Input
                  value={brief.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Ex : Refonte du parcours client"
                  autoFocus
                  data-testid="project-name-input"
                />
              </Field>
              <Field label="Type de projet">
                <Select value={brief.type} onValueChange={(v) => update("type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Secteur d'activité">
                <Input
                  value={brief.sector}
                  onChange={(e) => update("sector", e.target.value)}
                  placeholder="Ex : Retail, Santé, Secteur public…"
                />
              </Field>
            </>
          )}

          {step === 1 && (
            <>
              <Field label="Contexte" hint="Quelle situation justifie ce projet ?">
                <Textarea rows={4} value={brief.context} onChange={(e) => update("context", e.target.value)} />
              </Field>
              <Field label="Objectifs principaux">
                <Textarea rows={3} value={brief.objectives} onChange={(e) => update("objectives", e.target.value)} />
              </Field>
              <Field label="Critères de succès">
                <Textarea
                  rows={2}
                  value={brief.success_criteria}
                  onChange={(e) => update("success_criteria", e.target.value)}
                  placeholder="Ex : satisfaction client, gain de temps, ROI…"
                />
              </Field>
            </>
          )}

          {step === 2 && (
            <>
              <Field label="Périmètre">
                <Textarea
                  rows={3}
                  value={brief.scope}
                  onChange={(e) => update("scope", e.target.value)}
                  placeholder="Ce qui est inclus / exclu du projet"
                />
              </Field>
              <Field label="Contraintes">
                <Textarea
                  rows={3}
                  value={brief.constraints}
                  onChange={(e) => update("constraints", e.target.value)}
                  placeholder="Délais, budget, réglementaire, technique…"
                />
              </Field>
              <Field label="Cible / bénéficiaires">
                <Input value={brief.target_audience} onChange={(e) => update("target_audience", e.target.value)} />
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Durée estimée (mois)">
                  <Input
                    type="number"
                    min={1}
                    value={brief.duration_months}
                    onChange={(e) => update("duration_months", Number(e.target.value))}
                  />
                </Field>
                <Field label="Taille de l'équipe">
                  <Input
                    type="number"
                    min={1}
                    value={brief.team_size}
                    onChange={(e) => update("team_size", Number(e.target.value))}
                  />
                </Field>
              </div>
              <Field label="Budget estimé" hint="Ex : 30 000 - 50 000 €">
                <Input value={brief.budget_range} onChange={(e) => update("budget_range", e.target.value)} />
              </Field>
              <Field label="Parties prenantes clés" hint="Séparées par des virgules">
                <Input value={brief.key_stakeholders} onChange={(e) => update("key_stakeholders", e.target.value)} />
              </Field>
            </>
          )}

          {step === 4 && (
            <>
              <Field label="Forces">
                <Textarea rows={2} value={brief.strengths} onChange={(e) => update("strengths", e.target.value)} />
              </Field>
              <Field label="Faiblesses">
                <Textarea rows={2} value={brief.weaknesses} onChange={(e) => update("weaknesses", e.target.value)} />
              </Field>
              <Field label="Opportunités">
                <Textarea rows={2} value={brief.opportunities} onChange={(e) => update("opportunities", e.target.value)} />
              </Field>
              <Field label="Menaces">
                <Textarea rows={2} value={brief.threats} onChange={(e) => update("threats", e.target.value)} />
              </Field>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          className={step === 0 ? "opacity-0 pointer-events-none" : ""}
        >
          ← Précédent
        </Button>

        {step < STEPS.length - 1 ? (
          <Button
            onClick={() => canProceed() && setStep((s) => s + 1)}
            disabled={!canProceed()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Continuer →
          </Button>
        ) : (
          <Button
            onClick={handleCreate}
            disabled={creating}
            className="bg-blue-600 hover:bg-blue-700 text-white glow-blue"
            data-testid="generate-deliverables-btn"
          >
            {creating ? "Génération en cours…" : "Générer mes 13 livrables"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewProjectPage;